/**
 * Вкладка «История» — что происходило с макетом.
 *
 * Сырая лента врала масштабом: один прогон конвейера пишет по событию на
 * каждый товар, и «Файлы для печати готовы» повторялось десять раз подряд.
 * Поэтому подряд идущие одинаковые события по разным артикулам схлопываются в
 * одну строку «Визуалы готовы — 13 товаров» с раскрытием списка, пустые
 * переходы стадии «X → X» уходят в технические события, ошибки читаются
 * человеческим текстом (исходная строка — под «Техническим текстом»), а над
 * лентой стоят фильтры-чипы по разделам. Артикулы — ссылки на окно товара.
 */

import React, { useMemo, useState } from 'react'
import { ActivityTimeline, type ActivityEvent } from '../../../../components/ui/ActivityTimeline'
import { Badge } from '../../../../components/ui/Badge'
import { Skeleton } from '../../../../components/ui/Skeleton'
import { cn } from '../../../../lib/cn'
import { formatDateTime, pipelineStageInfo } from '../../lib'
import type { DesignV2 } from '../../types'
import type { HistoryEvent } from './events'
import { PipelineErrorDetails } from './PipelineErrorPlate'
import { productsCountLabel } from './pipelineError'

interface HistoryTabProps {
  design: DesignV2
  events: HistoryEvent[] | null
  /** Артикул в событии — ссылка на окно товара. */
  onOpenProduct?: (article: string) => void
  /** Префикс категории по артикулу — для краткой подписи групп ошибки. */
  prefixOf?: (article: string) => string | null
}

export type HistoryFilter = 'all' | 'pipeline' | 'files' | 'visuals' | 'cards' | 'errors'

const FILTERS: Array<{ key: HistoryFilter; label: string }> = [
  { key: 'all', label: 'Все' },
  { key: 'pipeline', label: 'Конвейер' },
  { key: 'files', label: 'Файлы' },
  { key: 'visuals', label: 'Визуалы' },
  { key: 'cards', label: 'Карточки' },
  { key: 'errors', label: 'Ошибки' },
]

/** Серия: события подряд, разделённые не более чем этим интервалом, считаются одним прогоном. */
export const SERIES_GAP_MS = 15 * 60 * 1000

const matches = (event: HistoryEvent, filter: HistoryFilter): boolean =>
  filter === 'all' ? true : filter === 'errors' ? event.isError : event.category === filter

const time = (event: HistoryEvent): number => {
  const value = new Date(event.at).getTime()
  return Number.isNaN(value) ? 0 : value
}

export interface HistoryRow {
  head: HistoryEvent
  /** Все события строки, от новых к старым. */
  events: HistoryEvent[]
  /** Артикулы серии без повторов. */
  articles: string[]
}

/**
 * Схлопывание. Серия по артикулам: подряд идущие события одного вида от одного
 * автора с артикулом, не дальше `SERIES_GAP_MS` друг от друга. Серия без
 * артикулов: подряд идущие события с тем же заголовком и описанием.
 * Ошибки и переходы стадий не схлопываются: у каждого свой текст.
 */
export function collapseHistory(events: HistoryEvent[]): HistoryRow[] {
  const rows: HistoryRow[] = []
  for (const event of events) {
    const previous = rows[rows.length - 1]
    if (previous && !event.isError && !event.diff) {
      const last = previous.events[previous.events.length - 1]
      const sameKind = last.title === event.title && last.type === event.type && last.actor === event.actor
      if (sameKind && event.article && last.article) {
        if (Math.abs(time(last) - time(event)) <= SERIES_GAP_MS) {
          previous.events.push(event)
          if (!previous.articles.includes(event.article)) previous.articles.push(event.article)
          continue
        }
      } else if (sameKind && !event.article && !last.article && last.detail === event.detail) {
        previous.events.push(event)
        continue
      }
    }
    rows.push({ head: event, events: [event], articles: event.article ? [event.article] : [] })
  }
  return rows
}

const ArticleButton: React.FC<{ article: string; onOpenProduct?: (article: string) => void }> = ({
  article,
  onOpenProduct,
}) =>
  onOpenProduct ? (
    <button
      type="button"
      title="Открыть товар"
      onClick={() => onOpenProduct(article)}
      className="font-mono text-indigo-700 hover:underline dark:text-indigo-300"
    >
      {article}
    </button>
  ) : (
    <span className="font-mono">{article}</span>
  )

const SeriesBody: React.FC<{ articles: string[]; onOpenProduct?: (article: string) => void }> = ({
  articles,
  onOpenProduct,
}) => {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="text-indigo-600 hover:underline dark:text-indigo-400"
      >
        {open ? 'Скрыть артикулы' : 'Показать артикулы'}
      </button>
      {open && (
        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
          {articles.map((article) => (
            <ArticleButton key={article} article={article} onOpenProduct={onOpenProduct} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Строка модели → событие для `ActivityTimeline`. */
export function rowToActivity(
  row: HistoryRow,
  onOpenProduct?: (article: string) => void,
  prefixOf?: (article: string) => string | null,
): ActivityEvent {
  const { head } = row
  const base: ActivityEvent = {
    id: head.id,
    at: head.at,
    actor: head.actor,
    type: head.type,
    title: head.title,
    diff: head.diff,
  }

  if (row.articles.length > 1) {
    return {
      ...base,
      title: `${head.title} — ${productsCountLabel(row.articles.length)}`,
      body: <SeriesBody articles={row.articles} onOpenProduct={onOpenProduct} />,
    }
  }

  // Одинаковые события без артикула: «× N», как раньше.
  const title = row.events.length > 1 ? `${head.title} × ${row.events.length}` : head.title
  const article = head.article ? <ArticleButton article={head.article} onOpenProduct={onOpenProduct} /> : null

  if (head.isError && head.rawError) {
    return {
      ...base,
      title,
      body: (
        <div className="space-y-1">
          {article}
          {head.context && <div className="text-gray-500 dark:text-zinc-400">{head.context}</div>}
          <PipelineErrorDetails
            raw={head.rawError}
            prefixOf={prefixOf}
            onOpenProduct={onOpenProduct}
            showHeadline={false}
          />
        </div>
      ),
    }
  }

  if (article) {
    return {
      ...base,
      title,
      body: (
        <>
          {article}
          {head.detail ? ` · ${head.detail}` : ''}
        </>
      ),
    }
  }
  return { ...base, title, description: head.detail ?? undefined }
}

export const HistoryTab: React.FC<HistoryTabProps> = ({ design, events, onOpenProduct, prefixOf }) => {
  const [showAll, setShowAll] = useState(false)
  const [filter, setFilter] = useState<HistoryFilter>('all')

  const model = useMemo(() => {
    if (!events) return null
    const meaningful = showAll ? events : events.filter((event) => !event.technical)
    const counts = Object.fromEntries(
      FILTERS.map(({ key }) => [key, meaningful.filter((event) => matches(event, key)).length]),
    ) as Record<HistoryFilter, number>
    return {
      rows: collapseHistory(meaningful.filter((event) => matches(event, filter))),
      counts,
      hiddenCount: events.length - meaningful.length,
    }
  }, [events, showAll, filter])

  const stage = pipelineStageInfo(design.pipeline_status)
  const timeline = useMemo(
    () => model?.rows.map((row) => rowToActivity(row, onOpenProduct, prefixOf)) ?? null,
    [model, onOpenProduct, prefixOf],
  )
  const hasEvents = (events?.length ?? 0) > 0

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-[0.8125rem] text-gray-500 dark:text-zinc-400">
        <span>Сейчас в конвейере:</span>
        {stage ? <Badge variant="blue">{stage.label}</Badge> : <span>—</span>}
        {model && model.hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="ml-auto text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Показать технические события ({model.hiddenCount})
          </button>
        )}
        {showAll && (
          <button
            type="button"
            onClick={() => setShowAll(false)}
            className="ml-auto text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Только важное
          </button>
        )}
      </div>

      {model && hasEvents && (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Фильтр событий">
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
                filter === key
                  ? 'border-indigo-300 bg-indigo-50 font-semibold text-indigo-800 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800',
                key === 'errors' && model.counts.errors > 0 && filter !== key && 'text-red-700 dark:text-red-300',
              )}
            >
              {label}
              <span className="tabular-nums text-gray-400 dark:text-zinc-500">{model.counts[key]}</span>
            </button>
          ))}
        </div>
      )}

      {timeline === null && <Skeleton variant="custom" className="rounded-md" height={160} />}
      {timeline !== null && timeline.length > 0 && <ActivityTimeline events={timeline} />}

      {timeline !== null && timeline.length === 0 && hasEvents && (
        <div className="px-2 py-6 text-center text-sm italic text-gray-500 dark:text-zinc-400">
          В этом разделе событий нет
        </div>
      )}

      {timeline !== null && timeline.length === 0 && !hasEvents && (
        <div className="flex flex-col gap-2.5 text-[0.8125rem]">
          <div className="text-xs text-gray-400 dark:text-zinc-500">
            Событий конвейера нет — макет попал в каталог импортом. Ключевые даты:
          </div>
          {[
            { label: 'Макет создан', at: design.created_at },
            { label: 'Макет принят', at: design.accepted_at },
            { label: 'Состояние изменено', at: design.status_changed_at },
            { label: 'Последнее обновление', at: design.updated_at },
          ]
            .filter((item) => item.at)
            .map((item) => (
              <div key={item.label} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-green-500" />
                <div>
                  <span className="text-gray-900 dark:text-zinc-100">{item.label}</span>
                  <div className="text-xs text-gray-500 dark:text-zinc-400">
                    {formatDateTime(item.at)}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

export default HistoryTab
